/**
 * Las reglas de la cuenta corriente de un cliente, separadas de la UI.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * Todo lo de aca decide CUANTO DEBE alguien, que es el numero por el que el
 * local discute en el mostrador. Y hay una trampa: un cliente con la cuenta
 * anclada al dolar tiene DOS numeros verdaderos al mismo tiempo — los dolares
 * que debe y los pesos que hay que cobrarle hoy. Elegir mal cual mostrar no es
 * un problema de diseño: es cobrar de menos.
 *
 * Ninguna funcion de aca toca React ni el DOM. Se prueban con numeros sueltos,
 * que es exactamente como hay que probar la plata.
 *
 * LA FORMA REAL DE LOS DATOS
 * --------------------------
 * `lib/tipos.ts` modela estas vistas incompletas (les faltan `saldo_ars_hoy`,
 * `saldo_ars_congelado`, `estado_mora`, `debe_ancla`...). Verificado con curl
 * contra el backend, `v_saldos_clientes` y `v_alertas_mora` traen mas columnas.
 * Aca se extienden con esos campos como OPCIONALES en vez de tocar `lib/`: asi
 * los tipos de `lib` siguen siendo asignables y no hay un `as unknown as` en
 * ninguna pantalla.
 */
import { convertir, formatearCotizacionNumero, redondear, type Moneda } from "@tora/shared";

import { ErrorApi } from "@/lib/api";
import {
  aNumero,
  type CambiosCliente,
  type Cliente,
  type FiltrosDeudas,
  type ModoCuentaCorriente,
  type NuevoCliente,
  type Numerico,
} from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// Las formas reales que devuelve el backend
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `v_saldos_clientes` tal como responde de verdad.
 *
 * Los campos que `lib/tipos.SaldoCliente` ya garantiza van requeridos; el resto
 * —que existe en la vista pero no esta modelado— va opcional. Asi un
 * `SaldoCliente` entra en un `SaldoCC` sin un solo `as`.
 */
export interface SaldoCC {
  cliente_id: number;
  nombre: string;
  cc_modo: ModoCuentaCorriente;
  /** En que moneda lleva el saldo esta cuenta. */
  moneda_anclaje: Moneda;
  /** El saldo, en `moneda_anclaje`. Positivo = debe. */
  saldo: Numerico;
  saldo_usd?: Numerico;
  saldo_ars?: Numerico;
  /** Suma de los importes congelados a la cotizacion de cada dia. */
  saldo_ars_congelado?: Numerico;
  /** Lo que hay que cobrarle HOY en pesos. Es el numero de la caja. */
  saldo_ars_hoy?: Numerico;
  movimientos?: Numerico;
  ultimo_cobro?: string | null;
  ultimo_movimiento?: string | null;
  limite_credito?: Numerico | null;
  limite_moneda?: Moneda | null;
  /**
   * Lo que dejo a cuenta de reparaciones que siguen en el taller, en
   * `moneda_anclaje` (0121_7). Ya NO esta restado de `saldo`: se usa al
   * entregar el equipo.
   */
  senas_ot?: Numerico | null;
}

/**
 * `v_alertas_mora`, la mitad de `GET /clientes/:id`.
 *
 * Ojo: la vista NO devuelve `dias_mora` aunque `lib/tipos.MoraCliente` lo
 * declare; lo que devuelve es `dias_sin_cobro` y `estado_mora`. Verificado con
 * curl contra el backend.
 */
export interface MoraCC {
  cliente_id: number;
  nombre: string;
  saldo: Numerico;
  /** Edad del comprobante impago mas viejo, en dias. */
  antiguedad_deuda: number | null;
  excedido: boolean;
  cc_modo?: ModoCuentaCorriente;
  moneda_anclaje?: Moneda;
  saldo_ars_hoy?: Numerico;
  estado_mora?: string;
  dias_sin_cobro?: number;
  comprobantes_impagos?: Numerico;
  comprobante_mas_viejo?: string | null;
  ultimo_cobro?: string | null;
  limite_credito?: Numerico | null;
  limite_moneda?: Moneda | null;
}

/**
 * Lo que `armarFilas()` necesita de una fila de `GET /deudas`.
 *
 * A proposito es un subconjunto y no `FilaDeuda` entero: la lista solo usa el
 * saldo, la moneda y el estado, y pedir la fila completa obligaria a inventar
 * las diez columnas restantes en cada test. `FilaDeuda` de `lib/tipos` entra
 * aca sin conversion.
 */
export interface FilaDeudaCC {
  cliente_id: number;
  nombre?: string;
  saldo: Numerico;
  /** Lo cobrable hoy en pesos. Es la unidad comun para comparar deudas. */
  saldo_ars_hoy?: Numerico;
  moneda_anclaje?: Moneda;
  antiguedad_deuda?: number | null;
  estado_mora?: string | null;
  excedido?: boolean;
}

/**
 * Una fila de `v_extracto_clientes`.
 *
 * `id` es `Numerico` y no `number` porque la vista lo devuelve como string: es
 * un `bigint` de Postgres, y esos viajan entre comillas.
 */
export interface FilaExtracto {
  id: Numerico;
  fecha: string;
  tipo: string;
  /** El importe que suma deuda, EN LA MONEDA DEL COMPROBANTE. */
  debe: Numerico;
  /** El importe que la baja, EN LA MONEDA DEL COMPROBANTE. */
  haber: Numerico;
  /** El acumulado, EN LA MONEDA DE ANCLAJE. No es la misma moneda que arriba. */
  saldo_acumulado: Numerico;
  /** La moneda del comprobante. */
  moneda: Moneda;
  cliente_id?: number;
  cliente?: string;
  detalle?: string | null;
  comprobante_id?: number | null;
  referencia?: string | null;
  /** La cotizacion a la que se asento ESTE movimiento. No la de hoy. */
  cotizacion?: Numerico | null;
  debe_ars?: Numerico;
  haber_ars?: Numerico;
  debe_usd?: Numerico;
  haber_usd?: Numerico;
  /** Lo que el movimiento sumo o resto EN LA MONEDA DE ANCLAJE. */
  debe_ancla?: Numerico;
  haber_ancla?: Numerico;
  moneda_anclaje?: Moneda;
}

// ─────────────────────────────────────────────────────────────────────────────
// El anclaje: la unica decision que importa de una cuenta corriente
// ─────────────────────────────────────────────────────────────────────────────

/**
 * En que queda parada la deuda: en pesos o en dolares.
 *
 * En la base son DOS campos (`cc_modo` y `cc_moneda`) pero una sola decision.
 * La regla, copiada literal de `v_saldos_clientes` y de `imputar_fifo()`:
 *
 *     anclaje = (cc_modo = 'USD' or cc_moneda = 'USD') ? USD : ARS
 *
 * O sea: una cuenta FIJA con `cc_moneda = USD` se comporta EXACTAMENTE igual
 * que una cuenta en modo USD — el saldo suma `debe_usd - haber_usd` y se
 * muestra al dolar de hoy. No es una tercera opcion, es la misma escrita de
 * otra forma. Por eso la pantalla ofrece dos y no tres: ofrecer una tercera que
 * hace lo mismo que la segunda solo sirve para que alguien elija mal.
 */
export type Anclaje = "PESOS" | "DOLAR";

export function monedaAnclaje(c: {
  cc_modo: ModoCuentaCorriente;
  cc_moneda?: Moneda | null;
}): Moneda {
  return c.cc_modo === "USD" || c.cc_moneda === "USD" ? "USD" : "ARS";
}

export function anclajeDe(c: { cc_modo: ModoCuentaCorriente; cc_moneda?: Moneda | null }): Anclaje {
  return monedaAnclaje(c) === "USD" ? "DOLAR" : "PESOS";
}

/** Los dos campos que hay que mandarle al backend para cada anclaje. */
export function camposDeAnclaje(a: Anclaje): { cc_modo: ModoCuentaCorriente; cc_moneda: Moneda } {
  return a === "DOLAR"
    ? { cc_modo: "USD", cc_moneda: "USD" }
    : { cc_modo: "FIJA", cc_moneda: "ARS" };
}

/**
 * La cotizacion como numero pelado, sin la unidad: "1.601".
 *
 * `formatearCotizacion()` de `@tora/shared` ya le pega el sufijo "ARS/USD", que
 * en una frase como "US$ 1 = $ 1.601" queda dicho dos veces. El formato (sin
 * decimales salvo que los tenga) es el de `formatearCotizacionNumero`.
 */
export function textoCotizacion(valor: number): string {
  return formatearCotizacionNumero(valor);
}

// ─────────────────────────────────────────────────────────────────────────────
// El saldo
// ─────────────────────────────────────────────────────────────────────────────

/** Medio centavo: abajo de eso la cuenta esta en cero y no "casi en cero". */
export const TOLERANCIA = 0.009;

export interface SaldoLegible {
  /** La moneda en la que la cuenta lleva el saldo. */
  moneda: Moneda;
  /** El saldo en esa moneda. Positivo = debe. Negativo = tiene a favor. */
  monto: number;
  debe: boolean;
  aFavor: boolean;
  /** true cuando la deuda se mueve sola con el dolar. */
  sigueAlDolar: boolean;
  /** Lo que hay que cobrarle HOY, en pesos. Es lo que entra a la caja. */
  cobrableHoyArs: number;
  /**
   * La cotizacion con la que se llego a `cobrableHoyArs`.
   * `null` cuando no hubo conversion (cuenta en pesos): ahi no hay nada que
   * aclarar porque no se convirtio nada.
   */
  cotizacion: number | null;
  /** "Debe" / "Tiene a favor" / "Sin deuda". */
  titulo: string;
}

/**
 * Los dos numeros de un cliente, y cual de los dos se cobra hoy.
 *
 * Para una cuenta en pesos hay uno solo y `cobrableHoyArs` es el mismo.
 * Para una cuenta en dolares hay dos y los dos son ciertos: los dolares son la
 * deuda, los pesos son la forma de cobrarla hoy. Manaña los pesos son otros.
 */
export function leerSaldo(saldo: SaldoCC | null | undefined, cotizacion: number): SaldoLegible {
  const moneda: Moneda = saldo?.moneda_anclaje ?? "ARS";
  const monto = redondear(aNumero(saldo?.saldo));
  const sigueAlDolar = moneda === "USD";

  // El backend ya calcula `saldo_ars_hoy` con `cotizacion_vigente()`. Se prefiere
  // ese numero antes que rehacer la cuenta aca: si el frontend multiplicara por
  // su propia cotizacion, dos pantallas mostrarian deudas distintas.
  const delBackend = saldo?.saldo_ars_hoy;
  const cobrableHoyArs = sigueAlDolar
    ? delBackend !== undefined && delBackend !== null
      ? redondear(aNumero(delBackend))
      : cotizacion > 0
        ? redondear(convertir(monto, "USD", "ARS", cotizacion))
        : 0
    : monto;

  const debe = monto > TOLERANCIA;
  const aFavor = monto < -TOLERANCIA;

  return {
    moneda,
    monto,
    debe,
    aFavor,
    sigueAlDolar,
    cobrableHoyArs,
    cotizacion: sigueAlDolar && cotizacion > 0 ? cotizacion : null,
    titulo: debe ? "Debe" : aFavor ? "Tiene a favor" : "Sin deuda",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Mora
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoMora = "SIN_DEUDA" | "AL_DIA" | "MORA_30" | "MORA_60" | "MORA_90";

export const TEXTO_MORA: Record<
  EstadoMora,
  { texto: string; variante: "secondary" | "warning" | "destructive" }
> = {
  SIN_DEUDA: { texto: "Sin deuda", variante: "secondary" },
  AL_DIA: { texto: "Al día", variante: "secondary" },
  MORA_30: { texto: "Debe hace +30 días", variante: "warning" },
  MORA_60: { texto: "Debe hace +60 días", variante: "warning" },
  MORA_90: { texto: "Debe hace +90 días", variante: "destructive" },
};

/**
 * En que estado de mora esta.
 *
 * Se cree lo que dice el backend (`estado_mora` de `v_alertas_mora`) y solo se
 * deduce de la antiguedad cuando esa columna no vino. La antiguedad es la edad
 * del comprobante impago mas viejo: el indicador que un pago chico no maquilla.
 */
export function estadoMoraDe(fila: {
  estado_mora?: string | null;
  antiguedad_deuda?: number | null;
  saldoArsHoy?: number;
}): EstadoMora {
  if (fila.saldoArsHoy !== undefined && fila.saldoArsHoy <= TOLERANCIA) return "SIN_DEUDA";

  const declarado = fila.estado_mora;
  if (
    declarado === "AL_DIA" ||
    declarado === "MORA_30" ||
    declarado === "MORA_60" ||
    declarado === "MORA_90"
  ) {
    return declarado;
  }

  const dias = fila.antiguedad_deuda ?? null;
  if (dias === null) return "AL_DIA";
  if (dias >= 90) return "MORA_90";
  if (dias >= 60) return "MORA_60";
  if (dias >= 30) return "MORA_30";
  return "AL_DIA";
}

// ─────────────────────────────────────────────────────────────────────────────
// La lista
// ─────────────────────────────────────────────────────────────────────────────

export interface FilaLista {
  cliente: Cliente;
  /** Saldo en la moneda de anclaje del cliente. */
  monto: number;
  moneda: Moneda;
  /** Lo cobrable hoy en pesos. Es la unidad comun para ORDENAR, no para sumar. */
  deudaArsHoy: number;
  mora: EstadoMora;
  excedido: boolean;
  antiguedad: number | null;
  /** false cuando el cliente todavia no tiene cuenta corriente abierta. */
  conCuenta: boolean;
}

/**
 * Lo que la solapa «Todos» le pide a `GET /deudas` para ponerle saldo a cada
 * fila.
 *
 * `todos: "1"` es lo que importa. Sin eso la ruta lee el recorte de alertas
 * (solo mora y excedidos), y como hoy nadie esta en mora, la lista entera decia
 * «sin deuda» — tambien a los que deben un millon. Con `todos` sale de la vista
 * completa y ordenada por nombre, que es el mismo orden y el mismo tope que el
 * listado de clientes: la pagina de saldos cubre la pagina de nombres.
 */
export function consultaSaldosDelDirectorio(limite: number, busqueda: string): FiltrosDeudas {
  return { todos: "1", limite, ...(busqueda ? { buscar: busqueda } : {}) };
}

/**
 * Cruza el listado de clientes con la vista de deudas.
 *
 * `GET /clientes` no trae saldo y `GET /deudas` no trae telefono: cada uno tiene
 * la mitad de la fila. Se juntan por id en vez de pedir el saldo cliente por
 * cliente, que serian N+1 consultas para dibujar una lista.
 */
export function armarFilas(
  clientes: ReadonlyArray<Cliente>,
  deudas: ReadonlyArray<FilaDeudaCC>,
): FilaLista[] {
  const porId = new Map<number, FilaDeudaCC>();
  for (const d of deudas) porId.set(d.cliente_id, d);

  return clientes.map((cliente) => {
    const d = porId.get(cliente.id);
    const moneda = monedaAnclaje(cliente);
    const deudaArsHoy = d ? redondear(aNumero(d.saldo_ars_hoy ?? d.saldo)) : 0;
    return {
      cliente,
      monto: d ? redondear(aNumero(d.saldo)) : 0,
      moneda,
      deudaArsHoy,
      mora: estadoMoraDe({
        estado_mora: d?.estado_mora,
        antiguedad_deuda: d?.antiguedad_deuda,
        saldoArsHoy: deudaArsHoy,
      }),
      excedido: d?.excedido === true,
      antiguedad: d?.antiguedad_deuda ?? null,
      conCuenta: d !== undefined,
    };
  });
}

/**
 * Los que deben, primero.
 *
 * El criterio de orden son los PESOS DE HOY y no el saldo en su moneda, porque
 * si no un cliente que debe US$ 300 quedaria abajo de uno que debe $ 5.000. No
 * se suman nunca: solo se comparan, que es una operacion legitima entre dos
 * montos llevados a la misma moneda con la misma cotizacion.
 *
 * Detras de los deudores va el resto en orden alfabetico: sin deuda que mirar,
 * lo unico util es encontrar el nombre.
 */
export function ordenarPorDeuda(filas: ReadonlyArray<FilaLista>): FilaLista[] {
  return [...filas].sort((a, b) => {
    const debeA = a.deudaArsHoy > TOLERANCIA;
    const debeB = b.deudaArsHoy > TOLERANCIA;
    if (debeA !== debeB) return debeA ? -1 : 1;
    if (debeA && debeB && a.deudaArsHoy !== b.deudaArsHoy) return b.deudaArsHoy - a.deudaArsHoy;
    return a.cliente.nombre.localeCompare(b.cliente.nombre, "es");
  });
}

/** Saca acentos y mayusculas: "Benitez" tiene que encontrar a "Benítez". */
export function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Busqueda por nombre. Tambien mira telefono, documento y codigo porque son las
 * otras tres cosas por las que alguien busca a un cliente, y no cuesta nada.
 */
export function filtrarFilas(filas: ReadonlyArray<FilaLista>, texto: string): FilaLista[] {
  const q = normalizar(texto);
  if (!q) return [...filas];
  return filas.filter(({ cliente }) => {
    const campos = [cliente.nombre, cliente.telefono, cliente.documento, cliente.codigo];
    return campos.some((c) => (c ? normalizar(c).includes(q) : false));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Limite de credito
// ─────────────────────────────────────────────────────────────────────────────

export interface Credito {
  limite: number;
  moneda: Moneda;
  /** El saldo llevado a la moneda del limite. */
  usado: number;
  disponible: number;
  excedido: boolean;
  /** Cuanto del limite esta consumido, 0-100+ (puede pasarse de 100). */
  porcentaje: number;
  /**
   * La cotizacion que hizo falta para comparar saldo contra limite.
   * `null` si estaban en la misma moneda y no se convirtio nada.
   */
  cotizacion: number | null;
}

/**
 * Cuanto credito le queda.
 *
 * Es el mismo calculo que `credito_disponible()` en la base: limite menos el
 * saldo CONVERTIDO a la moneda del limite. La conversion es inevitable —el
 * limite puede estar en dolares y la cuenta en pesos— y por eso la cotizacion
 * usada sale en el resultado: un disponible sin decir a que dolar se calculo es
 * un numero que no se puede auditar.
 */
export function creditoDisponible(params: {
  limite: Numerico | null | undefined;
  limiteMoneda: Moneda | null | undefined;
  saldo: number;
  monedaSaldo: Moneda;
  cotizacion: number;
}): Credito | null {
  const { limite, limiteMoneda, saldo, monedaSaldo, cotizacion } = params;
  if (limite === null || limite === undefined || limite === "") return null;

  const tope = aNumero(limite);
  if (tope <= 0) return null;

  const moneda: Moneda = limiteMoneda ?? "ARS";
  const hayQueConvertir = moneda !== monedaSaldo;
  if (hayQueConvertir && cotizacion <= 0) return null;

  const usado = redondear(
    hayQueConvertir ? convertir(saldo, monedaSaldo, moneda, cotizacion) : saldo,
  );
  const disponible = redondear(tope - usado);

  return {
    limite: tope,
    moneda,
    usado,
    disponible,
    excedido: disponible < -TOLERANCIA,
    porcentaje: Math.max(0, Math.round((usado / tope) * 100)),
    cotizacion: hayQueConvertir ? cotizacion : null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Cambiar el anclaje: por que casi nunca se puede
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El anclaje se puede tocar mientras la cuenta este vacia y no despues.
 *
 * El saldo no esta guardado en ningun lado: se calcula sumando columnas
 * distintas segun el anclaje. Cambiarlo con movimientos cargados no migra
 * nada — reinterpreta el historial entero, y las mismas ventas pasan a valer
 * otro numero sin que se haya cobrado ni facturado nada.
 *
 * El backend lo frena con un 409 `CC_CON_MOVIMIENTOS`. La pantalla no espera a
 * ese 409 para avisar: desactiva la opcion y explica por que.
 */
export function puedeCambiarAnclaje(movimientos: number | null | undefined): boolean {
  return (movimientos ?? 0) === 0;
}

/** El 409 del backend cuando alguien igual intento cambiarlo. */
export function esAnclajeBloqueado(error: unknown): boolean {
  return error instanceof ErrorApi && error.codigo === "CC_CON_MOVIMIENTOS";
}

export interface ImpactoAnclaje {
  /** Lo que la cuenta dice hoy, en pesos. */
  hoyArs: number;
  /** Lo que diria el MISMO historial con el otro anclaje, en pesos. */
  otroArs: number;
  diferenciaArs: number;
  anclajeActual: Anclaje;
}

/**
 * Cuanto cambiaria la deuda si se tocara el anclaje. La cifra que convierte
 * "no se puede" en "ah, mira por que no se puede".
 *
 * Se apoya en las dos columnas que la vista ya calcula: `saldo_ars_congelado`
 * (los pesos de cada movimiento al dolar de SU dia) y `saldo_ars_hoy` (los
 * dolares de la cuenta al dolar de HOY). Son las dos lecturas posibles del
 * mismo historial.
 */
export function impactoDeCambiarAnclaje(
  saldo: SaldoCC | null | undefined,
  cotizacion: number,
): ImpactoAnclaje | null {
  if (!saldo) return null;

  const anclaje: Anclaje = saldo.moneda_anclaje === "USD" ? "DOLAR" : "PESOS";
  const congelado = redondear(aNumero(saldo.saldo_ars_congelado));
  const enUsd = redondear(aNumero(saldo.saldo_usd));

  const hoyArs =
    anclaje === "DOLAR"
      ? saldo.saldo_ars_hoy !== undefined && saldo.saldo_ars_hoy !== null
        ? redondear(aNumero(saldo.saldo_ars_hoy))
        : cotizacion > 0
          ? redondear(enUsd * cotizacion)
          : 0
      : congelado;

  const otroArs =
    anclaje === "DOLAR" ? congelado : cotizacion > 0 ? redondear(enUsd * cotizacion) : 0;

  if (hoyArs === 0 && otroArs === 0) return null;

  return {
    hoyArs,
    otroArs,
    diferenciaArs: redondear(otroArs - hoyArs),
    anclajeActual: anclaje,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// El extracto
// ─────────────────────────────────────────────────────────────────────────────

export const ETIQUETA_TIPO_CC: Record<string, string> = {
  VENTA: "Venta",
  COBRO: "Cobro",
  NC: "Nota de crédito",
  ND: "Nota de débito",
  "AJUSTE+": "Ajuste a favor",
  "AJUSTE-": "Ajuste en contra",
  SALDO_INICIAL: "Saldo inicial",
};

export interface LineaExtracto {
  id: string;
  fecha: string;
  tipo: string;
  etiqueta: string;
  detalle: string;
  referencia: string | null;
  /** Cuanto sumo a la deuda, en la moneda de anclaje. */
  debe: number;
  /** Cuanto la bajo, en la moneda de anclaje. */
  haber: number;
  /** El saldo despues de este movimiento, en la moneda de anclaje. */
  saldo: number;
  moneda: Moneda;
  /**
   * El importe tal como se documento, cuando el comprobante estaba en OTRA
   * moneda que la de la cuenta. `null` si coinciden y no hay nada que aclarar.
   */
  original: { monto: number; moneda: Moneda; cotizacion: number | null } | null;
}

/**
 * Una fila del extracto lista para dibujar.
 *
 * LA PARTE DELICADA: `debe` / `haber` de la vista vienen en la moneda DEL
 * COMPROBANTE, mientras que `saldo_acumulado` viene en la moneda de ANCLAJE.
 * Ponerlos en columnas contiguas es mezclar pesos con dolares en la misma
 * lectura. Por eso las columnas usan `debe_ancla` / `haber_ancla` —el impacto
 * real en la cuenta— y el importe original, si difiere, se muestra aparte y con
 * la cotizacion a la que se asento.
 */
/**
 * La referencia que vale mostrar. La de un cobro es «COBRO-» mas los segundos
 * desde 1970 (`registrar_cobro`, 0006): «COBRO-1790747359» no le dice nada a
 * nadie, y la fecha ya esta en la fila. Se muestra el detalle en su lugar.
 */
function referenciaLegible(referencia: string | null | undefined): string | null {
  if (!referencia || /^COBRO-\d+$/.test(referencia)) return null;
  return referencia;
}

export function leerLineaExtracto(fila: FilaExtracto, anclaje: Moneda): LineaExtracto {
  const debeAncla = fila.debe_ancla;
  const haberAncla = fila.haber_ancla;

  // Si la vista no mando las columnas de anclaje (backend viejo), se cae a las
  // de la moneda que corresponde antes que a `debe` pelado, que es la del
  // comprobante y podria ser otra moneda.
  const debe = redondear(
    aNumero(debeAncla ?? (anclaje === "USD" ? fila.debe_usd : fila.debe_ars) ?? fila.debe),
  );
  const haber = redondear(
    aNumero(haberAncla ?? (anclaje === "USD" ? fila.haber_usd : fila.haber_ars) ?? fila.haber),
  );

  const montoPropio = redondear(aNumero(fila.debe) + aNumero(fila.haber));
  const cotizacion =
    fila.cotizacion !== undefined && fila.cotizacion !== null ? aNumero(fila.cotizacion) : null;

  return {
    id: String(fila.id),
    fecha: fila.fecha,
    tipo: fila.tipo,
    etiqueta: ETIQUETA_TIPO_CC[fila.tipo] ?? fila.tipo,
    // Un ajuste sin detalle guarda el tipo como detalle (`registrar_ajuste_cc`,
    // 0005), y al lado de la etiqueta se leia «AJUSTE-».
    detalle: fila.detalle && fila.detalle !== fila.tipo ? fila.detalle : "",
    referencia: referenciaLegible(fila.referencia),
    debe,
    haber,
    saldo: redondear(aNumero(fila.saldo_acumulado)),
    moneda: anclaje,
    original:
      fila.moneda !== anclaje && montoPropio > TOLERANCIA
        ? { monto: montoPropio, moneda: fila.moneda, cotizacion }
        : null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// El formulario
// ─────────────────────────────────────────────────────────────────────────────

export interface FormularioCliente {
  nombre: string;
  telefono: string;
  documento: string;
  email: string;
  direccion: string;
  localidad: string;
  observaciones: string;
  /**
   * `null` = todavia no se eligio. Solo pasa en el alta: una ficha existente
   * siempre tiene el suyo.
   */
  anclaje: Anclaje | null;
  /** `null` = sin limite de credito. NUNCA 0 por defecto. */
  limite: number | null;
  limiteMoneda: Moneda;
}

export const FORMULARIO_VACIO: FormularioCliente = {
  nombre: "",
  telefono: "",
  documento: "",
  email: "",
  direccion: "",
  localidad: "",
  observaciones: "",
  // Sin opcion marcada. Arrancaba en «PESOS», y la unica decision de la cuenta
  // que despues no se cambia se tomaba sola si nadie la miraba: un cliente
  // nuevo que compraba en dolares quedaba con la deuda congelada en pesos.
  anclaje: null,
  limite: null,
  limiteMoneda: "ARS",
};

export function formularioDesde(cliente: Cliente): FormularioCliente {
  return {
    nombre: cliente.nombre,
    telefono: cliente.telefono ?? "",
    documento: cliente.documento ?? "",
    email: cliente.email ?? "",
    direccion: cliente.direccion ?? "",
    localidad: cliente.localidad ?? "",
    observaciones: cliente.observaciones ?? "",
    anclaje: anclajeDe(cliente),
    limite:
      cliente.limite_credito === null || cliente.limite_credito === undefined
        ? null
        : aNumero(cliente.limite_credito),
    limiteMoneda: cliente.limite_moneda ?? "ARS",
  };
}

/**
 * El alta con lo que se busco antes de no encontrarlo: si son solo numeros (y
 * los signos de un telefono) va al telefono; si no, al nombre.
 */
export function formularioConBuscado(buscado: string): FormularioCliente {
  const t = buscado.trim();
  if (t === "") return FORMULARIO_VACIO;
  return /^[\d\s()+-]+$/.test(t) && /\d/.test(t)
    ? { ...FORMULARIO_VACIO, telefono: t }
    : { ...FORMULARIO_VACIO, nombre: t };
}

/** Lo minimo para poder guardar: nombre, un telefono para ubicarlo y el anclaje elegido. */
export function formularioListo(f: FormularioCliente): boolean {
  return (
    f.nombre.trim().length > 0 &&
    f.telefono.trim().length > 0 &&
    f.anclaje !== null &&
    !limiteNegativo(f)
  );
}

/**
 * Un tope negativo no existe. Antes pasaba a la revision («LÍMITE DE CRÉDITO
 * -$ 5.000,00») y recien al guardar el servidor contestaba 400: se frena en el
 * campo, donde se escribio.
 */
export function limiteNegativo(f: Pick<FormularioCliente, "limite">): boolean {
  return f.limite !== null && f.limite < 0;
}

function limpiar(valor: string): string | undefined {
  const t = valor.trim();
  return t === "" ? undefined : t;
}

/** El payload de alta. El anclaje siempre viaja: en el alta se puede elegir. */
export function armarAlta(f: FormularioCliente): NuevoCliente {
  // `formularioListo` no deja llegar hasta aca sin anclaje; si igual pasa, que
  // no se invente uno.
  if (f.anclaje === null) throw new Error("Falta elegir si la deuda sigue al dólar.");
  const { cc_modo, cc_moneda } = camposDeAnclaje(f.anclaje);
  return {
    nombre: f.nombre.trim(),
    cc_modo,
    cc_moneda,
    ...(limpiar(f.telefono) ? { telefono: limpiar(f.telefono) } : {}),
    ...(limpiar(f.documento) ? { documento: limpiar(f.documento) } : {}),
    ...(limpiar(f.email) ? { email: limpiar(f.email) } : {}),
    ...(limpiar(f.direccion) ? { direccion: limpiar(f.direccion) } : {}),
    ...(limpiar(f.localidad) ? { localidad: limpiar(f.localidad) } : {}),
    ...(limpiar(f.observaciones) ? { observaciones: limpiar(f.observaciones) } : {}),
    // El limite viaja con su moneda o no viaja: "limite 1000" sin moneda no
    // dice nada, y el backend lo rechaza con razon.
    ...(f.limite !== null ? { limite_credito: f.limite, limite_moneda: f.limiteMoneda } : {}),
  };
}

/**
 * El payload de edicion: SOLO lo que cambio.
 *
 * Lo importante es lo que NO manda. Si el anclaje quedo igual, `cc_modo` y
 * `cc_moneda` no se mandan — mandar el valor actual seria inofensivo en teoria,
 * pero un cliente con `cc_modo = USD` y `cc_moneda = ARS` (existen: es el
 * default de la tabla) recibiria un `cc_moneda: USD` "de paso" y el backend lo
 * contaria como un cambio de anclaje y devolveria 409. Un formulario no puede
 * romperse por un campo que el usuario ni miro.
 */
export function armarCambios(
  f: FormularioCliente,
  actual: Cliente,
  puedeAnclaje: boolean,
): CambiosCliente {
  const cambios: CambiosCliente = {};

  const nombre = f.nombre.trim();
  if (nombre && nombre !== actual.nombre) cambios.nombre = nombre;

  const textos: Array<[keyof FormularioCliente & keyof CambiosCliente, string | null]> = [
    ["telefono", actual.telefono],
    ["documento", actual.documento],
    ["email", actual.email],
    ["direccion", actual.direccion],
    ["localidad", actual.localidad],
    ["observaciones", actual.observaciones],
  ];
  for (const [campo, anterior] of textos) {
    const nuevo = String(f[campo] ?? "").trim();
    if (nuevo !== (anterior ?? "")) {
      // El backend no acepta cadena vacia (`min(1)`), asi que borrar un campo se
      // manda como espacio en blanco no: directamente no se manda. Vaciar un
      // telefono desde aca no esta soportado por el contrato.
      if (nuevo !== "") (cambios as Record<string, unknown>)[campo] = nuevo;
    }
  }

  const limiteAnterior =
    actual.limite_credito === null || actual.limite_credito === undefined
      ? null
      : aNumero(actual.limite_credito);
  const monedaAnterior = actual.limite_moneda ?? "ARS";
  if (f.limite !== limiteAnterior || (f.limite !== null && f.limiteMoneda !== monedaAnterior)) {
    cambios.limite_credito = f.limite;
    // Va siempre junto: el backend exige la moneda cada vez que se toca el limite.
    if (f.limite !== null) cambios.limite_moneda = f.limiteMoneda;
  }

  if (puedeAnclaje && f.anclaje !== null && f.anclaje !== anclajeDe(actual)) {
    const { cc_modo, cc_moneda } = camposDeAnclaje(f.anclaje);
    cambios.cc_modo = cc_modo;
    cambios.cc_moneda = cc_moneda;
  }

  return cambios;
}

export function hayCambios(cambios: CambiosCliente): boolean {
  return Object.keys(cambios).length > 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Totales de la lista
// ─────────────────────────────────────────────────────────────────────────────

export interface TotalesDeuda {
  /** Lo que deben las cuentas llevadas en pesos. */
  ars: number;
  /** Lo que deben las cuentas ancladas al dolar, EN DOLARES. */
  usd: number;
  cuentasArs: number;
  cuentasUsd: number;
}

/**
 * Cuanto hay afuera, partido por moneda de anclaje.
 *
 * No hay un total: hay dos. Sumar los pesos de las cuentas fijas con los dolares
 * de las ancladas daria un numero que no es la deuda de nadie. El consolidado no
 * se ofrece aca a proposito — para eso esta la cotizacion del dia y la pantalla
 * de cuentas corrientes, donde ademas se cobra.
 */
export function totalizarDeuda(filas: ReadonlyArray<FilaLista>): TotalesDeuda {
  return filas.reduce<TotalesDeuda>(
    (acc, f) => {
      if (f.monto <= TOLERANCIA) return acc;
      if (f.moneda === "USD") {
        acc.usd = redondear(acc.usd + f.monto);
        acc.cuentasUsd += 1;
      } else {
        acc.ars = redondear(acc.ars + f.monto);
        acc.cuentasArs += 1;
      }
      return acc;
    },
    { ars: 0, usd: 0, cuentasArs: 0, cuentasUsd: 0 },
  );
}
